import './sales-call-calendar.css';
import { Modal, Skeleton, App } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { CalendarSlot, Lead, Result, Slot } from '../../dto/model';
import { salesAppointmentsService } from '../../services/sales-appointments-service';
import { CalendarHeader } from './calendar-header';
import { CalendarBody } from './calendar-body';
import { CalendarParameters } from './calendar-parameters';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import BookResponse from './book-response';
import LoadingBar from 'react-top-loading-bar';
import { strings } from '../../lang';
import { DoNotCloseTag } from '../common/donotclose-tag';
import { getCurrentUtcDate, getNextFiveIsoWeeks } from '../../utils/time-utils';

export interface CalendarInfo {
  opportunityId: string;
  signupId: string;
  onBooking: (isSuccess: boolean, bookedSlot?: Slot, error?: string) => void;
  preBooking?: (oppId: string) => Promise<void>;
  outerModalClose?: () => void;
  displayUserInfo?: boolean;
  skipConfirmationAfterBooking?: boolean;
  isInbound?: boolean;
  sourceSystemHeader: string;
  salesChannel?: string;
  salesManagerId?: string;
}

export const SalesCallCalendar: React.FC<CalendarInfo> = (calendarInfo) => {
  const { message, notification } = App.useApp();
  const { instance, inProgress, accounts } = useMsal();
  const loadingBarRef = useRef(null);

  const { opportunityId, signupId, onBooking, displayUserInfo, skipConfirmationAfterBooking, salesChannel, sourceSystemHeader, salesManagerId } = calendarInfo;
  const [slots, setSlots] = useState<CalendarSlot[]>();
  const [lead, setLead] = useState<Lead>();
  const [week, setWeek] = useState<string>(getNextFiveIsoWeeks()[0]);
  const [selectedDay] = useState<number>(1);
  const [bookedSlotResult, setBookedSlotResult] = useState<Result<Slot>>();
  const [slotToBook, setSlotToBook] = useState<CalendarSlot>();
  const [isBooking, setIsBooking] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [calendarLoadedDate, setCalendarLoadedDate] = useState<string>();
  const cy = Number.parseInt(week.split(/-/g)[0], 10);
  const cw = Number.parseInt(week.split(/-/g)[1], 10);

  const bookSlot = async (slot: CalendarSlot) => {
    // If the sales manager ID is present, we cannot book a slot as this is a read-only calendar
    if (salesManagerId) return;
    setSlotToBook(slot);
  };

  const cancelBookingSlot = () => {
    setSlotToBook(undefined);
    setBookedSlotResult(undefined);
  };

  const closeConfirmationModal = () => {
    setBookedSlotResult(undefined);
    setSlotToBook(undefined);
    setIsLoading(false);
    if (calendarInfo.isInbound) {
      calendarInfo.outerModalClose?.();
    }
  };

  const confirmBookingSlot = async () => {
    if (bookedSlotResult?.isSuccess) {
      window.close();
      return;
    }

    if (!slotToBook || isBooking) {
      return;
    }

    try {
      setIsLoading(true);
      setIsBooking(true);

      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

      // check if calendar was loaded on a different day
      const currentUtcDate = getCurrentUtcDate();
      if (calendarLoadedDate && calendarLoadedDate !== currentUtcDate) {
        message.info('Calendar was loaded on a different day. Refreshing slots...');

        const calendarData = await salesAppointmentsService.getAvailableSlots(token.accessToken, week, opportunityId, signupId, salesChannel, sourceSystemHeader, salesManagerId);
        setSlots(calendarData.slots);
        const slotStillExists = calendarData.slots.some((s) => s.startDate?.isSame(slotToBook.startDate));

        if (!slotStillExists) {
          setIsLoading(false);
          setIsBooking(false);
          message.error('The selected slot is no longer available. Please select a different slot.');
          setSlotToBook(undefined);
          return;
        }
      }

      if (calendarInfo.preBooking) {
        await calendarInfo.preBooking(opportunityId);
      }

      if (!opportunityId && !signupId) {
        return;
      }
      // when opportunityId is present, (signupId and salesChannel) should be absent and vice versa
      const bookedSlot = await salesAppointmentsService.bookSlot(
        opportunityId,
        signupId,
        slotToBook.startDate,
        token.accessToken,
        sourceSystemHeader,
        salesChannel,
        sourceSystemHeader === 'inbound-center' ? 'reschedule' : undefined
      );

      if (bookedSlot.isSuccess && skipConfirmationAfterBooking === true) {
        setSlotToBook(undefined);
        setBookedSlotResult(undefined);
      } else {
        setBookedSlotResult(bookedSlot);
      }

      onBooking(bookedSlot.isSuccess, bookedSlot.data, bookedSlot.errors?.join(', '));
    } catch {
      setSlotToBook(undefined);
      onBooking(false, undefined);
    } finally {
      setIsBooking(false);
      setIsLoading(false);
    }
  };

  /*
  Checks if the booking result has a fatal error.
  If the error is NO_SLOTS_AVAILABLE, then the user should be allowed to pick another slot,
    but if the error is something else, the UI must not allow the user to perform any other action.
  */
  const hasBookingError = (bookingResult: Result<Slot> | undefined) => {
    return bookingResult && !bookingResult.isSuccess && !bookingResult.errors?.find((x) => x.includes('NO_SLOTS_AVAILABLE'));
  };

  useEffect(() => {
    if (inProgress === 'none') {
      // If the booking result threw an error, then reload appointments.
      // Otherwise do nothing, since it does an extra useless call to the backend.
      if (bookedSlotResult && !hasBookingError(bookedSlotResult)) {
        return;
      }

      GetAccessToken(instance, inProgress, scopes.salesAppointmentApi).then(async (token) => {
        if (!token) {
          return;
        }
        try {
          // when opportunityId is present, (signupId and salesChannel) should be absent and vice versa
          const calendarData = await salesAppointmentsService.getAvailableSlots(token.accessToken, week, opportunityId, signupId, salesChannel, sourceSystemHeader, salesManagerId);
          //If we try to load the standalone calendar without the Lead Object in it, it means the customer was not found
          if (!calendarData.lead.opportunityId && opportunityId && !salesManagerId) {
            notification.open({
              key: 'errorNotification',
              message: strings.noOpportunityInCalendarTitle,
              description: <p>{strings.noOpportunityInCalendarMessage}</p>,
              placement: 'topRight',
              duration: 0,
              type: 'error',
            });
          }
          setLead(calendarData.lead);
          setSlots(calendarData.slots);
        } catch {
          message.error('Error loading the calendar');
        }
      });
    }
  }, [inProgress, accounts, instance, bookedSlotResult, salesManagerId, opportunityId, salesChannel, signupId]);

  useEffect(() => {
    if (loadingBarRef.current) {
      if (isLoading) {
        (loadingBarRef.current as any).staticStart();
      } else {
        (loadingBarRef.current as any).complete();
      }
    }
  }, [isLoading]);

  // Prevents the user to close or refresh the page while booking.
  useEffect(() => {
    window.onbeforeunload = isBooking ? () => 1 : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [isBooking]);

  useEffect(() => {
    setCalendarLoadedDate(getCurrentUtcDate());
  }, [slots]);

  if (!slots || !lead) {
    return (
      <div className="empty-container">
        <Skeleton loading={true}></Skeleton>
      </div>
    );
  }

  return (
    <>
      <LoadingBar color="var(--color-primary)" ref={loadingBarRef} shadow={true} />
      <div className="cal-page">
        <CalendarParameters
          lead={lead}
          displayUserInfo={!!displayUserInfo}
          isLoading={isLoading}
          onRefresh={async (data) => {
            setIsLoading(true);
            try {
              const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
              const calendarSlots = await salesAppointmentsService.getAvailableSlots(
                token.accessToken,
                `${data.calendarYear}-${data.calendarWeek.toString().padStart(2, '0')}`,
                opportunityId,
                signupId,
                salesChannel,
                sourceSystemHeader,
                salesManagerId
              );

              setWeek(`${data.calendarYear}-${data.calendarWeek.toString().padStart(2, '0')}`);
              setSlots(calendarSlots.slots);
            } finally {
              setIsLoading(false);
            }
          }}
        ></CalendarParameters>

        <CalendarHeader calendarWeek={cw} calendarYear={cy} selectedDay={selectedDay}></CalendarHeader>

        <CalendarBody slots={slots} onBookSlot={bookSlot} isLoading={isLoading} />

        <Modal
          title={`Booking`}
          width={600}
          open={slotToBook !== undefined}
          onCancel={cancelBookingSlot}
          onOk={bookedSlotResult?.isSuccess ? closeConfirmationModal : confirmBookingSlot}
          closable={false}
          okText={bookedSlotResult?.isSuccess ? strings.close : 'Ok'}
          cancelText={strings.cancel}
          okButtonProps={{
            loading: isBooking,
            disabled: hasBookingError(bookedSlotResult),
          }}
          cancelButtonProps={{
            disabled: isBooking || bookedSlotResult?.isSuccess,
          }}
          maskClosable={false}
          keyboard={false}
        >
          <BookResponse slotToBook={slotToBook!} bookedSlot={bookedSlotResult} />
          {isBooking && <DoNotCloseTag />}
        </Modal>
      </div>
    </>
  );
};
